import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as fromAdmin from '@app/client/account/admin/reducers';
import { IamService } from '@app/core/services/iam.service';
import { TokenService } from '@app/core/services/token.service';
import * as fromRoot from '@app/reducers';
import { detectDelimiter } from '@app/shared/helpers/csv';
import { buildDates, formatDate } from '@app/shared/helpers/helpers';
import { isEmail } from '@app/shared/helpers/validators';
import { User } from '@app/shared/models/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Iam } from '@smash-sdk/iam/ejs/10-2019';
import { Transfer } from '@smash-sdk/transfer/ejs/01-2024';
import moment from 'moment';
import { NgxCsvParser } from 'ngx-csv-parser';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, map, mergeMap, retryWhen, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { MembersActions } from '../actions';

const SCIMGroupName = 'SCIM';

@Injectable()
export class MembersEffects {

  addRootRoleToMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.addRootRoleToMember),
      concatMap((action) => {
        return this.iamService.attachUserRole(action.member.id, Constant.administratorRoleName).pipe(
          concatMap(() => {
            return [MembersActions.addRootRoleToMemberSuccess(), MembersActions.loadMember({ id: action.member.id })];
          }),
          catchError(error =>
            of(MembersActions.addRootRoleToMemberFailure({ error: error.message }))
          )
        );
      }))
  );

  removeRootRoleToMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.removeRootRoleFromMember),
      concatMap((action) => {
        return this.iamService.detachUserRole(action.member.id, Constant.administratorRoleName).pipe(
          concatMap(() => {
            return [MembersActions.removeRootRoleFromMemberSuccess(), MembersActions.loadMember({ id: action.member.id })];
          }),
          catchError(error =>
            of(MembersActions.removeRootRoleFromMemberFailure({ error: error.message }))
          )
        );
      }))
  );

  loadMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMember),
      concatMap((action) => {
        return forkJoin([this.iamService.getUser(action.id), this.iamService.listUserRoles(action.id)]).pipe(
          map(([resMember, resUserRoles]) => {
            const { user } = resMember;
            const { roles } = resUserRoles;
            return MembersActions.loadMemberSuccess({ member: { ...user, isRoot: !!roles.find(role => role.name === Constant.administratorRoleName) } });
          }),
          catchError(error =>
            of(MembersActions.loadMemberFailure({ error: error.message }))
          )
        );
      }))
  );

  loadMemberDomains$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberDomains),
      withLatestFrom(this.store$.pipe(select(fromAdmin.getAccountTeams))),
      concatMap(([action, accountTeams]) => {
        const reqs = [
          this.iamService.listRoles().pipe(defaultIfEmpty({ roles: [] })),
          this.iamService.listUserRoles(action.id).pipe(defaultIfEmpty({ roles: [] })),
          this.iamService.listUserGroupsRolesWithGroupId(action.id).pipe(defaultIfEmpty([]))
        ];
        return forkJoin(reqs).pipe(
          concatMap(([resAccountRoles, resRoles, resGroupRoles]: any[]) => {
            const { roles: accountRoles = [] } = resAccountRoles;
            const { roles: rolesDirectlyLinkToUser = [] } = resRoles;
            const rolesLinkedToUserGroups = resGroupRoles;
            const roles = [...rolesDirectlyLinkToUser, ...rolesLinkedToUserGroups];

            const rolesWithoutManagedRoles = roles.filter(role => !role.name.startsWith('Managed'));
            // Mapping users teams with roles
            const teamsMappedWithRoles = rolesWithoutManagedRoles.map(role => {
              const team = accountTeams.find(accountTeam => accountTeam.id === role.name.split('$')[0]);
              const roleNameTranslated = role?.groupName ? this.translateService.instant(`${role.name.split('$')[1].toUpperCase()}`) + ` (` + this.translateService.instant('VIA_GROUP', { group: role.groupName }) + `)` : this.translateService.instant(`${role.name.split('$')[1].toUpperCase()}`);
              return {
                teamId: team.id,
                teamName: team.name,
                roleNameTranslated,
                role,
                groupId: role?.d ? role.d : null,
                groupName: role?.groupName ? role.groupName : null, // Used to display or not the group in domains table
              };
            });

            // Compute which team and associated roles are available to attach to user
            const attributableDomains = accountTeams.map(team => {
              const filteredRoles = accountRoles.filter(accountRole => !rolesWithoutManagedRoles.some(role => accountRole.name === role.name) && accountRole.name.split('$')[0] === team.id);
              return { ...team, roles: filteredRoles };
            }).filter((team: any) => team.roles.length !== 0);

            return [MembersActions.loadMemberDomainsSuccess({ domains: teamsMappedWithRoles, attributableDomains })];
          }),
          catchError(error =>
            of(MembersActions.loadMemberDomainsFailure({ error: error.message }))
          )
        );
      }))
  );

  loadMemberGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberGroups),
      concatMap((action) => {
        return forkJoin(
          [
            this.iamService.listUserGroups(action.id),
            this.iamService.listGroups(),
          ]
        ).pipe(
          map(([resGroups, resAccountGroups]: any[]) => {
            const { groups: memberGroups } = resGroups;
            const { groups: accountGroups } = resAccountGroups;
            const filteredAccountGroups = accountGroups.filter(accountGroup => accountGroup.name !== SCIMGroupName && !accountGroup.provisioned);
            const attributableGroups = filteredAccountGroups.filter(accountGroup => !memberGroups.find(memberGroup => accountGroup.id === memberGroup.id));
            return MembersActions.loadMemberGroupsSuccess({ groups: memberGroups, attributableGroups });
          }),
          catchError(error =>
            of(MembersActions.loadMemberGroupsFailure({ error: error.message }))
          )
        );
      }))
  );

  loadMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMembers),
      withLatestFrom(this.store$.pipe(select(fromRoot.getActiveIdentity))),
      switchMap(([action, identity]) => {
        const SCIMUsername = this.tokenService.getAccountFromToken(identity.token.token) + '$scim';
        
        const iamSdk = new Iam();
        
        const loadAllUsers = (accumulatedUsers: User[] = [], start?: string): Observable<User[]> => {
          return from(iamSdk.listUsers({ start })).pipe(
            concatMap((response: any) => {
              const { users, next } = response;
              const newAccumulatedUsers = [...accumulatedUsers, ...users] as User[];
              
              if (next) {
                return loadAllUsers(newAccumulatedUsers, next);
              }
              
              return of(newAccumulatedUsers);
            })
          );
        };

        return loadAllUsers().pipe(
          map((allUsers: User[]) => {
            const usersFiltered = allUsers.filter(user => user.username !== SCIMUsername);
            return MembersActions.loadMembersSuccess({ members: usersFiltered });
          }),
          catchError(error =>
            of(MembersActions.loadMembersFailure({ error: error.message }))
          )
        );
      })
    )
  );

  loadAttributableRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadAttributableRoles),
      switchMap(action =>
        this.iamService.listRoles().pipe(
          map((response) => {
            return MembersActions.loadAttributableRolesSuccess({ roles: response.roles });
          }),
          catchError(error =>
            of(MembersActions.loadAttributableRolesFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadAttributableGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadAttributableGroups),
      switchMap(() =>
        this.iamService.listGroups().pipe(
          map(({ groups }: any) => {
            const attributableGroups = groups.filter(accountGroup => accountGroup.name !== SCIMGroupName && !accountGroup.provisioned);
            return MembersActions.loadAttributableGroupsSuccess({ groups: attributableGroups });
          }),
          catchError(error =>
            of(MembersActions.loadAttributableGroupsFailure({ error: error.message }))
          )
        )
      )
    )
  );


  addMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.addMember),
      withLatestFrom(this.store$.pipe(select(fromRoot.getSelectedLanguage))),
      concatMap(([action, language]) => {
        return this.iamService.createUser({ ...action.member, language }).pipe(
          concatMap(({ user }) => {
            const batchAttachUserToTeam = [];
            const batchAttachRoleToUser = [];
            const batchAttachUserToGroup = [];
            if (action.teams.length) {
              action.teams.forEach(team => {
                batchAttachUserToTeam.push(this.iamService.attachUserToTeam(team.team.id, user.id));
                batchAttachRoleToUser.push(this.iamService.attachUserRole(user.id, team.role.name));
              });
              return forkJoin(batchAttachUserToTeam).pipe(concatMap(() => {
                return forkJoin(batchAttachRoleToUser).pipe(map(() => {
                  return MembersActions.addMemberSuccess({ member: user });
                }));
              }));
            }
            if (action.groups.length) {
              action.groups.forEach(groups => {
                batchAttachUserToGroup.push(this.iamService.attachUserToGroup(groups.group.id, user.id));
              });
              return forkJoin(batchAttachUserToGroup).pipe(map(() => {
                return MembersActions.addMemberSuccess({ member: user });
              }));
            }
          }),
          retryWhen(error =>
            error.pipe(
              take(3),
              map((error: HttpErrorResponse) => {
                if (!(error.status >= 500)) {
                  throw error;
                }
              })
            )
          ),
          catchError((error: HttpErrorResponse) => {
            return of(MembersActions.addMemberFailure({ error: error.error }));
          })
        );
      })
    )
  );

  initImportMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.initImportMembers),
      map((action) => {
        return MembersActions.checkContentFile({ fileMembers: action.fileMembers, csvContent: action.csvContent });
      })
    )
  );


  checkContentFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.checkContentFile),
      switchMap((action) => {
        const delimiter = detectDelimiter(action.csvContent);
        return this.iamService.listGroups().pipe(concatMap(({ groups: accountGroups }) => {
          return this.ngxCsvParser.parse(action.fileMembers[0], { header: false, delimiter }).pipe(concatMap((rows: any[]) => {
            // Removing headers
            rows.shift();
            let errorDetails = '';
            if (rows.length > 0) {
              // Looking for error in CSV and building error report
              for (let index = 0; index < rows.length; index++) {
                let errorRow = '';
                if (rows[index].length === 5) {
                  const [username, authenticationMode, groups] = rows[index];
                  const trimmedUsername = username.trim();
                  const trimmedAuthenticationMode = authenticationMode.trim();
                  if (!isEmail(trimmedUsername)) {
                    errorRow += ` Invalid email : ${username ? '\'' + username + '\'' : 'Empty email'}.`;
                  } else if (rows.find((row) => row.username === username)) {
                    errorRow += ` Same email on another row`;
                  }
                  if (!(trimmedAuthenticationMode === Constant.signinMode.sso || trimmedAuthenticationMode === Constant.signinMode.standard)) {
                    errorRow += ` Invalid authentication mode : '${trimmedAuthenticationMode}'. Choose either '${Constant.signinMode.sso}' or '${Constant.signinMode.standard}' (case sensitive).`;
                  }
                  const parsedGroupNames = groups.split(',').map(group => group.trim());
                  if (parsedGroupNames?.length > 0) {
                    parsedGroupNames.forEach(groupName => {
                      if (!accountGroups.find(group => group.name === groupName)) {
                        errorRow += ` Invalid group name : ${groupName ? '\'' + groupName + '\'' : 'undefined'}.`;
                      }
                    });
                  } else {
                    errorRow += ` Empty or invalid group list. At least one group is required. Check if group names are separated by a dash.`;
                  }
                } else {
                  errorRow += `Row format is incorrect. Check for extra columns or unescaped commas.`;
                }
                if (!!errorRow) {
                  errorDetails += `Row ${index} - ${errorRow} \n`;
                }
              }
            } else {
              errorDetails += `Empty CSV`;
            }
            if (errorDetails.length > 0) {
              const error = { date: moment().format('YYYY-MM-DD'), details: errorDetails };
              return [MembersActions.checkContentFileFailure({ error })];
            }
            // Building array of members to create
            const members = rows.map(([username, authenticationMode, groups, firstName, lastName]) => {
              return {
                username: username.trim(),
                authenticationMode: authenticationMode.trim(),
                groups: groups.split(',').map(group => group.trim()),
                firstName: firstName.trim(),
                lastName: lastName.trim(),
              }
            });
            return [MembersActions.importMembers({ members })];
          }), catchError((error: any) => [MembersActions.checkContentFileFailure({ error })])
          )
        }));
      })
    )
  );

  importMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.importMembers),
      withLatestFrom(this.store$.pipe(select(fromRoot.getSelectedLanguage))),
      switchMap(async ([action, language]) => {
        const { members } = action;
        const { groups: accountGroups } = await this.iamService.listGroups().toPromise();
        let errorDetails = '';
        let importedMembers = [];
        const { users: existingMembers } = await this.iamService.listUsers().toPromise();
        for (const member of members) {
          const existingMember = existingMembers.find(item => member.username === item.username);
          if (existingMember) {
            const groupsToAttach = accountGroups.filter(group => member.groups.find(groupName => groupName === group.name));
            await Promise.all(
              groupsToAttach.map(group => this.iamService.attachUserToGroup(group.id, existingMember.id).toPromise()
                .catch((error: HttpErrorResponse) => {
                  if (error.status === 409) {
                    errorDetails += `Failed to attach member '${member.username}' to group '${group.name}'. User already attached to this group.\n`;
                  }
                })
              )
            );
            importedMembers = [...importedMembers, existingMember];
            this.store$.dispatch(MembersActions.UpdateImportMembersStatus({ importedMembers }));
          } else {
            const memberToCreate = { ...member, language };
            const resMemberCreated = await this.iamService.createUser(memberToCreate).toPromise()
              .catch((error: HttpErrorResponse) => {
                if (error.status === 409) {
                  errorDetails += `Failed to create member '${member.username}'. Email already in use. User has not been attached to corresponding groups.\n`;
                }
              });
            if (resMemberCreated && resMemberCreated.user) {
              if (resMemberCreated.user.authenticationMode === Constant.signinMode.standard) {
                await this.iamService.inviteUser(resMemberCreated.user.id).toPromise();
              }
              const groupsToAttach = accountGroups.filter(group => member.groups.find(groupName => groupName === group.name));
              await Promise.all(groupsToAttach.map(group => this.iamService.attachUserToGroup(group.id, resMemberCreated.user.id).toPromise()));
              importedMembers = [...importedMembers, existingMember];
              this.store$.dispatch(MembersActions.UpdateImportMembersStatus({ importedMembers }));
            }
          }
        }
        if (errorDetails.length > 0) {
          const nonBlockingError = { date: moment().format('YYYY-MM-DD'), details: errorDetails };
          return MembersActions.importMembersSuccess({ members: importedMembers, nonBlockingError });
        } else {
          return MembersActions.importMembersSuccess({ members: importedMembers });
        }
      })
    )
  );

  attachMemberToTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.attachMemberToTeam),
      switchMap(action => {
        return this.iamService.listUserRoles(action.member.id).pipe(concatMap(({ roles }) => {
          const isUserAttachedToTeam = roles.some(role => role.name.split('$')[0] === action.team.id);
          const reqs = [this.iamService.attachUserRole(action.member.id, action.role.name)];
          if (!isUserAttachedToTeam) {
            reqs.push(this.iamService.attachUserToTeam(action.team.id, action.member.id));
          }
          return forkJoin(reqs).pipe(
            concatMap(() => {
              return [
                MembersActions.attachMemberToTeamSuccess(),
                MembersActions.loadMemberDomains({ id: action.member.id })
              ];
            }),
            catchError(error =>
              of(MembersActions.attachMemberToTeamFailure({ error }))
            )
          );
        }));
      }))
  );

  detachMemberFromTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.detachMemberFromTeam),
      switchMap(action => {
        const { team, member } = action;
        return this.iamService.listUserRoles(action.member.id).pipe(concatMap(resRoles => {
          const { roles } = resRoles;
          const userTeamRoles = roles.filter(role => role.name.split('$')[0] === team.teamId);
          return this.iamService.detachUserRole(member.id, team.role.name).pipe(
            concatMap(() => {
              // Detach user from team if it was the last role to detach from this team
              if (userTeamRoles.length === 1) {
                return this.iamService.detachUserFromTeam(team.teamId, member.id).pipe(concatMap(() => {
                  return [
                    MembersActions.detachMemberFromTeamSuccess(),
                    // MembersActions.loadMemberDomains({ id: member.id })
                  ];
                }));
              } else {
                return [
                  MembersActions.detachMemberFromTeamSuccess(),
                  // MembersActions.loadMemberDomains({ id: member.id })
                ];
              }
            }),
            catchError(error =>
              of(MembersActions.detachMemberFromTeamFailure({ error: error.status })))
          );
        }));
      }))
  );

  attachMemberToGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.attachMemberToGroup),
      switchMap(action => {
        const { member, group } = action;
        return this.iamService.attachUserToGroup(group.id, member.id).pipe(
          concatMap(() => {
            return [
              MembersActions.attachMemberToGroupSuccess(),
              MembersActions.loadMemberGroups({ id: member.id }),
              MembersActions.loadMemberDomains({ id: member.id }),
            ];
          }),
          catchError(error =>
            of(MembersActions.attachMemberToGroupFailure({ error: error.message }))
          )
        );
      })));

  detachMemberFromGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.detachMemberFromGroup),
      switchMap(action => {
        const { group, member } = action;
        return this.iamService.detachUserFromGroup(group.id, member.id).pipe(
          concatMap(() => {
            return [
              MembersActions.detachMemberFromGroupSuccess(),
              MembersActions.loadMemberGroups({ id: member.id }),
              MembersActions.loadMemberDomains({ id: member.id }),
            ];
          }),
          catchError(error =>
            of(MembersActions.detachMemberFromGroupFailure({ error: error.message }))
          )
        );
      })));

  deleteMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.deleteMember),
      switchMap((action: any) => {
        return forkJoin([
          this.iamService.listUserTeams(action.member.id),
          this.iamService.listUserRoles(action.member.id),
          this.iamService.listUserGroups(action.member.id),
        ]).pipe(
          concatMap(([resTeams, resRoles, resGroups]: any[]) => {
            const batchDetachTeams = resTeams.teams.map(team => this.iamService.detachUserFromTeam(team.id, action.member.id));
            const batchDetachUserTeamRoles = resRoles.roles.map(role => this.iamService.detachUserRole(action.member.id, role.name));
            const batchDetachUserGroups = resGroups.groups.map(group => this.iamService.detachUserFromGroup(group.id, action.member.id));
            return forkJoin([...batchDetachTeams, ...batchDetachUserTeamRoles, ...batchDetachUserGroups]).pipe(defaultIfEmpty(null), concatMap(() => {
              return this.iamService.deleteUser(action.member.id).pipe(
                mergeMap(() => {
                  return [MembersActions.deleteMemberSuccess(), MembersActions.loadMembers()];
                }),
                catchError((error: HttpErrorResponse) =>
                  of(MembersActions.deleteMemberFailure({ error: error.message }))
                )
              );
            }));
          }),
          catchError(error =>
            of(MembersActions.deleteMemberFailure({ error: error.message }))
          )
        );
      })
    )
  );

  updateMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.updateMember),
      withLatestFrom(this.store$.pipe(select(fromAdmin.getMemberDetails))),
      concatMap(([action, memberDetails]: any[]) => {
        if (memberDetails?.provisioned) {
          return [MembersActions.updateMemberFailure({ error: `Member is managed by provisioning system, can't modify related informations` })];
        }
        return this.iamService.updateUser(memberDetails.id, action.data).pipe(
          concatMap(response => {
            return [
              MembersActions.updateMemberSuccess({ member: response.user }),
              MembersActions.loadMember({ id: response.user.id })
            ];
          }),
          catchError((error: HttpErrorResponse) => {
            return [MembersActions.updateMemberFailure({ error: error.error })];
          }
          )
        );
      }
      )
    )
  );

  inviteMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.inviteMember),
      map(action => action.id),
      concatMap((memberId) =>
        this.iamService.inviteUser(memberId).pipe(
          map((response: any) => {
            return MembersActions.inviteMemberSuccess();
          }),
          catchError((error: HttpErrorResponse) => {
            return [MembersActions.inviteMemberFailure({ error: error.message })];
          }
          )
        )
      )
    )
  );

  switchMemberRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.switchMemberRole),
      switchMap(action => {
        return forkJoin([
          this.iamService.detachUserRole(action.member.id, action.roleToDettach.name),
          this.iamService.attachUserRole(action.member.id, action.roleToAttach.name)
        ]).pipe(
          map((response: any) => {
            return MembersActions.switchMemberRoleSuccess();
          }),
          catchError(error =>
            of(MembersActions.switchMemberRoleFailure({ error }))
          )
        );
      }))
  );

  loadMemberTransferCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberTransferCount),
      concatMap((action) => {
        const { user, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'transfers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getUserMetricStatistic({ metricName, statisticName, userId: user, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const currentLanguage = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            const transferCountSeries = statistic.data.map(counter => ({ ...counter, date: formatDate(currentLanguage, counter.date, unit) }));
            return MembersActions.loadMemberTransferCountSuccess({ transferCountSeries });
          }),
          catchError(error =>
            of(MembersActions.loadMemberTransferCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadMemberSizeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberSizeCount),
      concatMap((action) => {
        const { user, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'bytesUploaded';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getUserMetricStatistic({ metricName, statisticName, userId: user, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const currentLanguage = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            const sizeCountSeries = statistic.data.map(counter => ({ ...counter, date: formatDate(currentLanguage, counter.date, unit) }));
            return MembersActions.loadMemberSizeCountSuccess({ sizeCountSeries });
          }),
          catchError(error =>
            of(MembersActions.loadMemberSizeCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadMemberFileCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberFileCount),
      concatMap((action) => {
        const { user, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'files';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getUserMetricStatistic({ metricName, statisticName, userId: user, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const currentLanguage = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            const fileCountSeries = statistic.data.map(counter => ({ ...counter, date: formatDate(currentLanguage, counter.date, unit) }));
            return MembersActions.loadMemberFileCountSuccess({ fileCountSeries });
          }),
          catchError(error =>
            of(MembersActions.loadMemberFileCountFailure({ error: error.message }))
          ));
      })
    )
  );

  constructor(
    private actions$: Actions,
    private translateService: TranslateService,
    private iamService: IamService,
    private tokenService: TokenService,
    private store$: Store<fromRoot.State & fromAdmin.State>,
    private ngxCsvParser: NgxCsvParser,
  ) { }

}
